import React, { useState, Fragment } from 'react';
import cx from 'classnames';
import _uniqueId from 'lodash/uniqueId';
import Tooltip from '../Tooltip/Tooltip';
import styles from './createIconComponent.module.less';

export type CreateIconComponentProps = {
  /** SVG content without the top level svg element */
  children?: React.ReactNode;
  /** A descriptive title for the icon for a11y support.
   * Unfortunately, also the text of a tooltip if useZolaTooltip={true}
   * */
  title?: string;
  width?: number;
  height?: number;
  unit?: string;
  /** Color of the main content of the icon. Preferred over fill for 3.0 icons.
   *  Default 'currentColor'
   * */
  color?: string;
  /**
   * Fill color of the icon.
   * Default 'currentColor'
   * @deprecated Use color if icon supports it
   */
  fill?: string;
  fillOpacity?: string;
  className?: string;
  viewBox?: string;
  style?: React.CSSProperties;
  rotateDeg?: number;
  flip?: 'horizontal' | 'vertical';
  direction?: 'up' | 'right' | 'down' | 'left';
  rest?: unknown;
  [x: string]: unknown;
  useZolaTooltip?: boolean;

  /**
   * Show the title element providing a11y support.  Default: true, show the title element.
   * Use this to disable the if you have provided explicit screen reader text along with this icon.
   */
  showTitle?: boolean;
};

/**
 * Base component for SVG Icons
 *
 * Below is a gallery of SVG icon components with their `ICON_KEY` listed below.
 *
 * ## Usage
 * ###### For V3:
 * - Always a named export
 * - Should match an UpperCased name of the snake-cased-icon from Figma
 * - Numbers that start icon names (e.g 2fa) are renamed to be valid JS vars (e.g TwoFa)
 *
 * ```
 * import ICON_KEY from '@zola/zola-ui/src/components/SvgIcons/ICON_KEY_WITHOUT_ICON';
 * // V3 Example:
 * import { HomeHeartIcon } from '@zola/zola-ui/src/components/SvgIconsV3/HomeHeart';
 * ...
 *
 * <ICON_KEY />
 * ```
 *
 * ## Contributing
 * To add an icon component, follow the instructions [here](https://github.com/NewAmsterdamLabs/zola-ui/wiki/Adding-a-new-SVG-Icon)
 */
const CreateIconComponent: React.FC<CreateIconComponentProps> = ({
  children,
  title,
  width = 16,
  height = 16,
  unit = 'px',
  color = 'currentColor',
  fill = 'currentColor',
  className,
  viewBox,
  style = {},
  rotateDeg,
  direction,
  flip,
  useZolaTooltip = false,
  showTitle = true,
  ...rest
}) => {
  const [titleId] = useState(_uniqueId(title));
  const [showTooltip, setShowTooltip] = useState(false);

  const addunit = (val: number) => `${val}${unit}`;
  const defaultViewBox = viewBox || `0 0 ${width} ${height}`;
  const rotateStyle: { [transform: string]: string } = {};
  if (rotateDeg) {
    rotateStyle.transform = `rotate(${rotateDeg}deg)`;
  } else if (!rotateDeg && direction) {
    switch (direction) {
      // Direction assumes the icon is 'pointing' down
      case 'up':
        rotateStyle.transform = `rotate(180deg)`;
        break;
      case 'right':
        rotateStyle.transform = `rotate(270deg)`;
        break;
      case 'down':
        rotateStyle.transform = `rotate(0deg)`;
        break;
      case 'left':
        rotateStyle.transform = `rotate(90deg)`;
        break;
      default:
        rotateStyle.transform = `rotate(0deg)`;
        break;
    }
  }
  const styleAttr = Object.assign(
    {},
    {
      width: addunit(width),
      height: addunit(height),
      ...rotateStyle,
      ...(flip && { transform: flip === 'vertical' ? 'scale(-1)' : 'scale(-1,1)' }),
    },
    style
  ); // will help IE11, especially if child of flexbox
  const classes = cx('zui-svg-icon', className);

  return (
    <Fragment>
      {useZolaTooltip ? (
        <span
          className={styles.container}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onMouseDown={() => setShowTooltip(false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            aria-labelledby={titleId}
            role="img"
            className={classes}
            width={style.width}
            height={style.height}
            viewBox={defaultViewBox}
            color={color}
            fill={fill}
            style={styleAttr}
            {...rest}
          >
            {children}
          </svg>
          {title && showTooltip && <Tooltip text={title} className="icon-tooltip" {...rest} />}
        </span>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          aria-labelledby={showTitle ? titleId : undefined}
          role="img"
          className={classes}
          width={style.width}
          height={style.height}
          viewBox={defaultViewBox}
          color={color}
          fill={fill}
          style={styleAttr}
          {...rest}
        >
          {showTitle && <title id={titleId}>{title}</title>}
          {children}
        </svg>
      )}
    </Fragment>
  );
};

export default CreateIconComponent;
